import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { IViewConfig } from "lola-framework-ui-test";
import type { IErrorState } from "../shared/error/interfaces/error";
import { errorInitialState, themeFormattedInitialState } from "../constants";
import type { IAmountState } from "../interfaces/amount";
import type { IPaymentState } from "../interfaces/payment";
import type { ICardState } from "../interfaces/card";
import type { IQuoteResponse } from "../services/api";
import type { FundingInstrument } from "../interfaces/funding";
import { fundingSelectionForPersist } from "../utils/persistFundingSelection";

interface GlobalState {
  theme: IViewConfig;
  setTheme: (theme: IViewConfig) => void;

  linkId: string | null;
  setLinkId: (linkId: string | null) => void;

  errorState: IErrorState;
  setErrorState: (errorState: IErrorState) => void;

  amountState: IAmountState;
  setAmountState: (amountState: IAmountState) => void;

  paymentState: IPaymentState;
  setPaymentState: (paymentState: IPaymentState) => void;

  cardState: ICardState;
  setCardState: (cardState: ICardState) => void;

  cardList: ICardState[];
  setCardList: (cardList: ICardState[]) => void;

  quoteState: IQuoteResponse | null;
  setQuoteState: (quoteState: IQuoteResponse | null) => void;

  /** Saved funding card. Register happens at add-card; rail prepare on confirm. */
  fundingInstrument: FundingInstrument | null;
  setFundingInstrument: (fundingInstrument: FundingInstrument | null) => void;

  /** Set after successful prepareFundingRail on confirm (CARD only). */
  preparedFundingPaymentMethodId: string | null;
  setPreparedFundingPaymentMethodId: (id: string | null) => void;

  /** Set after payout rail prepare on confirm when payout compose is editable. */
  preparedPayoutPaymentMethodId: string | null;
  setPreparedPayoutPaymentMethodId: (id: string | null) => void;

  /** Canonical funding method pinned across add-card / instrument pick. */
  pendingFundingMethod: string | null;
  setPendingFundingMethod: (method: string | null) => void;

  /** Wipe transfer compose when a new magic link is claimed (send parity). */
  resetComposeState: () => void;
}

const cardStateInitial = {
  cardId: null,
  name: null,
  brand: null,
  last4: null,
  accountNumber: null,
  expirationDate: null,
  fullName: null,
  cvv: null,
} satisfies ICardState;

const composeInitialState = {
  amountState: {
    amount: "",
    anchor: "receive" as const,
  },
  paymentState: {
    payment: null,
  },
  cardState: cardStateInitial,
  cardList: [] as ICardState[],
  quoteState: null as IQuoteResponse | null,
  fundingInstrument: null as FundingInstrument | null,
  preparedFundingPaymentMethodId: null as string | null,
  preparedPayoutPaymentMethodId: null as string | null,
  pendingFundingMethod: null as string | null,
};

export const useStore = create<GlobalState>()(
  persist(
    (set) => ({
      theme: themeFormattedInitialState,
      setTheme: (theme) => {
        set(() => ({ theme }));
      },

      linkId: null,
      setLinkId: (linkId) => {
        set(() => ({ linkId }));
      },

      errorState: errorInitialState,
      setErrorState: (errorState) => {
        set(() => ({ errorState }));
      },

      amountState: composeInitialState.amountState,
      setAmountState: (amountState) => {
        set(() => ({ amountState }));
      },

      paymentState: composeInitialState.paymentState,
      setPaymentState: (paymentState) => {
        set(() => ({ paymentState }));
      },

      cardState: composeInitialState.cardState,
      setCardState: (cardState) => {
        set(() => ({ cardState }));
      },

      cardList: composeInitialState.cardList,
      setCardList: (cardList) => {
        set(() => ({ cardList }));
      },

      quoteState: composeInitialState.quoteState,
      setQuoteState: (quoteState) => {
        set(() => ({ quoteState }));
      },

      fundingInstrument: composeInitialState.fundingInstrument,
      setFundingInstrument: (fundingInstrument) => {
        set(() => ({ fundingInstrument }));
      },

      preparedFundingPaymentMethodId:
        composeInitialState.preparedFundingPaymentMethodId,
      setPreparedFundingPaymentMethodId: (preparedFundingPaymentMethodId) => {
        set(() => ({ preparedFundingPaymentMethodId }));
      },

      preparedPayoutPaymentMethodId:
        composeInitialState.preparedPayoutPaymentMethodId,
      setPreparedPayoutPaymentMethodId: (preparedPayoutPaymentMethodId) => {
        set(() => ({ preparedPayoutPaymentMethodId }));
      },

      pendingFundingMethod: composeInitialState.pendingFundingMethod,
      setPendingFundingMethod: (pendingFundingMethod) => {
        set(() => ({ pendingFundingMethod }));
      },

      resetComposeState: () => {
        set(() => ({ ...composeInitialState }));
      },
    }),
    {
      name: "raas-widgets-flow-fund-request-commit",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        quoteState: state.quoteState,
        preparedFundingPaymentMethodId: state.preparedFundingPaymentMethodId,
        ...fundingSelectionForPersist(state),
      }),
    },
  ),
);
